// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Currency, SerializableCurrency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';

/**
 * A cost field whose amount the user actually edited.
 *
 * The section dropdown owns the currency; a field's isoCode exists only so the amount
 * serialises as a currency object, and the dropdown cascades a new one onto every field
 * when it changes. That cascade is not user intent, so a dirty isoCode alone must not
 * make the form restate an amount the user never touched.
 */
export function amountChanged(
  field: { amount?: boolean; isoCode?: boolean } | undefined
): boolean {
  return Boolean(field?.amount);
}

/**
 * Cost fields common to item and asset accounting records. Both accounting
 * shapes carry these as nullable currencies (useful life as a number).
 */
export interface AccountingCostSource {
  usefulLife: number | null;
  salvageValue: Currency | null;
  simpleCostBasis: Currency | null;
  billOfMaterials: Currency | null;
  directLabor: Currency | null;
  allocatedIndirect: Currency | null;
  freightInbound: Currency | null;
  freightOutbound: Currency | null;
  installation: Currency | null;
  tax: Currency | null;
  tariffs: Currency | null;
  interest: Currency | null;
}

/** The accounting cost fields in their react-hook-form input shape. */
export interface AccountingCostFormValues {
  usefulLife: string | null;
  salvageValue: SerializableCurrency;
  simpleCostBasis: SerializableCurrency;
  billOfMaterials: SerializableCurrency;
  directLabor: SerializableCurrency;
  allocatedIndirect: SerializableCurrency;
  freightInbound: SerializableCurrency;
  freightOutbound: SerializableCurrency;
  installation: SerializableCurrency;
  tax: SerializableCurrency;
  tariffs: SerializableCurrency;
  interest: SerializableCurrency;
}

/**
 * Seeds the accounting cost fields into their form shape, denominating every
 * money field in currency. Callers layer on the accounting currency (and any
 * form-specific fields) and may override simpleCostBasis with a computed sum.
 */
export function toAccountingCostFormValues(
  source: AccountingCostSource,
  currencyCode: IsoCurrencyCode
): AccountingCostFormValues {
  return {
    usefulLife: source.usefulLife?.toString() ?? null,
    salvageValue: toCurrencyInputValue(source.salvageValue, currencyCode),
    simpleCostBasis: toCurrencyInputValue(source.simpleCostBasis, currencyCode),
    billOfMaterials: toCurrencyInputValue(source.billOfMaterials, currencyCode),
    directLabor: toCurrencyInputValue(source.directLabor, currencyCode),
    allocatedIndirect: toCurrencyInputValue(
      source.allocatedIndirect,
      currencyCode
    ),
    freightInbound: toCurrencyInputValue(source.freightInbound, currencyCode),
    freightOutbound: toCurrencyInputValue(source.freightOutbound, currencyCode),
    installation: toCurrencyInputValue(source.installation, currencyCode),
    tax: toCurrencyInputValue(source.tax, currencyCode),
    tariffs: toCurrencyInputValue(source.tariffs, currencyCode),
    interest: toCurrencyInputValue(source.interest, currencyCode),
  };
}

/** The components that make up an advanced cost breakdown. */
const ADVANCED_COST_FIELDS = [
  'billOfMaterials',
  'directLabor',
  'allocatedIndirect',
  'freightInbound',
  'freightOutbound',
  'installation',
  'tax',
  'tariffs',
  'interest',
] as const;

/** The accounting cost fields after the resolver has parsed them. */
export type AccountingCostFormOutput = Record<
  (typeof ADVANCED_COST_FIELDS)[number] | 'salvageValue' | 'simpleCostBasis',
  Currency | null
>;

/** Which cost fields the user edited, as react-hook-form reports them. */
export type AccountingCostDirtyFields = Partial<
  Record<
    keyof AccountingCostFormOutput,
    { amount?: boolean; isoCode?: boolean } | undefined
  >
>;

/** The cost fields shared by the item and asset accounting update payloads. */
export type CostBreakdownParameters = Partial<
  Record<keyof AccountingCostFormOutput, Currency | null>
>;

/**
 * Builds the cost half of an accounting update from the amounts the user edited.
 *
 * The two cost modes are mutually exclusive on the wire, so the mode on screen nulls
 * the other one outright — advanced clears the simple basis, simple clears every
 * component — whether or not those fields were touched.
 */
export function costBreakdownParameters(
  data: AccountingCostFormOutput,
  dirtyFields: AccountingCostDirtyFields,
  showAdvancedBreakdown: boolean
): CostBreakdownParameters {
  const parameters: CostBreakdownParameters = {};

  if (amountChanged(dirtyFields.salvageValue)) {
    parameters.salvageValue = data.salvageValue;
  }

  if (showAdvancedBreakdown) {
    parameters.simpleCostBasis = null;
    for (const field of ADVANCED_COST_FIELDS) {
      if (amountChanged(dirtyFields[field])) {
        parameters[field] = data[field];
      }
    }
    return parameters;
  }

  if (amountChanged(dirtyFields.simpleCostBasis)) {
    parameters.simpleCostBasis = data.simpleCostBasis;
  }
  for (const field of ADVANCED_COST_FIELDS) {
    parameters[field] = null;
  }
  return parameters;
}
